/**
 * Shared user-context prompt block (AXIS X23/X24/X25). The ONE place "who the user is" is turned into
 * LLM prompt text: display name, the free-text biography (General Settings), the set of email addresses
 * that belong to the user (so a composer can detect self-sent mail and sign correctly), and — for a
 * reply — the specific mailbox it is composing AS, with that account's identity block (name/sign-off/
 * role). Injected by every LLM feature (drafts, triage, brief, meeting prep, Slack replies) so identity
 * has a single source of truth.
 *
 * Pure string building — no I/O. See `docs/plans/action-system.md` §3.6 + PU phase.
 */

/** Per-mailbox identity the composer signs as (X25). Stored in `user.preferences.accountIdentities`. */
export interface AccountIdentity {
  displayName?: string;
  /** e.g. "— Evan" / "Best, Evan Ruff". */
  signoff?: string;
  /** e.g. "CTO, OXOS". */
  role?: string;
}

/**
 * One labeled contact entry — a mailing address or a phone number, with a user-typed label ("Home",
 * "Work", "Lake house", …). `primary` marks the single default Lapo picks when it can only use one
 * (a voice call's callback number, a form's shipping address). `id` is a stable key for the editor.
 */
export interface ContactEntry {
  id: string;
  /** Free-text label; may be empty. */
  label: string;
  /** The mailing address (multi-line allowed) or phone number. */
  value: string;
  /** The default entry for single-pick uses (DIAL, form-fill). At most one per list. */
  primary?: boolean;
}

/**
 * The user's contact details (General Settings). Stored in `user.preferences.contact` and surfaced in
 * the shared prompt block so Lapo knows where to ship things, which number to give out on a call, or
 * how to fill an address/phone field when it acts on the user's behalf. Any number of labeled
 * addresses and phones; legacy fixed-field data is migrated on read by {@link normalizeUserContact}.
 */
export interface UserContact {
  addresses?: ContactEntry[];
  phones?: ContactEntry[];
}

/** The legacy fixed-field contact shape (pre-flexible-lists), still on disk until a profile is re-saved. */
interface LegacyContact {
  homeAddress?: string;
  officeAddress?: string;
  mobilePhone?: string;
  homePhone?: string;
  officePhone?: string;
}

/** The single entry a one-pick consumer (DIAL, form-fill) should use: the primary, else the first. */
export function primaryEntry(list: ContactEntry[] | undefined): ContactEntry | undefined {
  if (!list?.length) return undefined;
  return list.find((e) => e.primary) ?? list[0];
}

/** Primary-first ordering for prompt rendering, without mutating the caller's array. */
export function orderedEntries(list: ContactEntry[] | undefined): ContactEntry[] {
  if (!list?.length) return [];
  const primary = list.filter((e) => e.primary);
  return [...primary, ...list.filter((e) => !e.primary)];
}

let migratedIdSeq = 0;
function cleanEntries(raw: unknown, maxValue: number): ContactEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: ContactEntry[] = [];
  let sawPrimary = false;
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const r = item as Record<string, unknown>;
    const value = typeof r.value === 'string' ? r.value.trim().slice(0, maxValue) : '';
    if (!value) continue; // drop empty rows (in-progress edits never persist)
    const label = typeof r.label === 'string' ? r.label.trim().slice(0, CONTACT_LABEL_MAX_CHARS) : '';
    const id = typeof r.id === 'string' && r.id ? r.id : `c${(migratedIdSeq += 1)}`;
    // Keep at most one primary — the first flagged wins, the rest are demoted.
    const primary = r.primary === true && !sawPrimary;
    if (primary) sawPrimary = true;
    out.push({ id, label, value, ...(primary ? { primary: true } : {}) });
  }
  return out;
}

/** Migrate the legacy fixed-field shape → labeled lists (first entry of each list is the primary). */
function migrateLegacy(c: LegacyContact): UserContact {
  const addresses = cleanEntries(
    [
      { id: 'legacy-home-address', label: 'Home', value: c.homeAddress, primary: true },
      { id: 'legacy-office-address', label: 'Work', value: c.officeAddress },
    ],
    CONTACT_ADDRESS_MAX_CHARS,
  );
  const phones = cleanEntries(
    [
      { id: 'legacy-mobile-phone', label: 'Mobile', value: c.mobilePhone, primary: true },
      { id: 'legacy-home-phone', label: 'Home', value: c.homePhone },
      { id: 'legacy-office-phone', label: 'Work', value: c.officePhone },
    ],
    CONTACT_PHONE_MAX_CHARS,
  );
  // Re-flag primaries: migrateLegacy marks the first source row primary, but cleanEntries may have
  // dropped it (empty), so ensure the surviving first entry becomes the default.
  if (addresses[0] && !addresses.some((a) => a.primary)) addresses[0].primary = true;
  if (phones[0] && !phones.some((p) => p.primary)) phones[0].primary = true;
  const out: UserContact = {};
  if (addresses.length) out.addresses = addresses;
  if (phones.length) out.phones = phones;
  return out;
}

/**
 * Coerce whatever is stored in `preferences.contact` (legacy fixed fields OR the new labeled lists)
 * into a clean {@link UserContact}: trimmed, length-clamped, empty rows dropped, at most one primary
 * per list. Safe on `undefined`/garbage (returns `{}`). The one place old data is migrated forward.
 */
export function normalizeUserContact(raw: unknown): UserContact {
  if (!raw || typeof raw !== 'object') return {};
  const r = raw as Record<string, unknown>;
  // New shape: has an addresses/phones array. Legacy shape: has one of the old string fields.
  if (Array.isArray(r.addresses) || Array.isArray(r.phones)) {
    const out: UserContact = {};
    const addresses = cleanEntries(r.addresses, CONTACT_ADDRESS_MAX_CHARS);
    const phones = cleanEntries(r.phones, CONTACT_PHONE_MAX_CHARS);
    if (addresses.length) out.addresses = addresses;
    if (phones.length) out.phones = phones;
    return out;
  }
  return migrateLegacy(r as LegacyContact);
}

export interface UserContextInput {
  displayName?: string;
  /** The free-text "about me" from General Settings (X24). ~20k cap enforced at the API. */
  biography?: string;
  /** Home/office addresses + phone numbers from General Settings. */
  contact?: UserContact;
  /** Every email address that belongs to the user (all linked gmail accounts). */
  ownedEmails: string[];
  /** The mailbox this prompt is composing AS (a reply's thread owner) — X23/X25. */
  actingAs?: { email: string; identity?: AccountIdentity };
}

/** Biography length cap (X24) — enforced by the profile API; re-clamped here defensively. */
export const BIOGRAPHY_MAX_CHARS = 20000;

/** Per-field cap for a mailing address (enforced at the API; re-clamped here defensively). */
export const CONTACT_ADDRESS_MAX_CHARS = 500;
/** Per-field cap for a phone number (enforced at the API; re-clamped here defensively). */
export const CONTACT_PHONE_MAX_CHARS = 50;
/** Cap for a contact entry's free-text label ("Home", "Lake house", …). */
export const CONTACT_LABEL_MAX_CHARS = 60;
/** Max labeled entries per list (addresses / phones) — a sanity bound on the profile API. */
export const CONTACT_MAX_ENTRIES = 50;

/**
 * Build the compact plaintext context block prepended to an LLM prompt. Returns '' when there is
 * nothing to say (no empty scaffolding — asserted by the PU golden fixtures).
 */
export function userContextBlock(input: UserContextInput): string {
  const lines: string[] = [];
  const acting = input.actingAs;
  const actingName = acting?.identity?.displayName || input.displayName;
  if (input.displayName || actingName) {
    lines.push(`The user you are working for is ${actingName || input.displayName}.`);
  }
  if (acting?.identity?.role) lines.push(`In this mailbox they are: ${acting.identity.role}.`);
  if (input.biography?.trim()) {
    lines.push(`About the user: ${input.biography.trim().slice(0, BIOGRAPHY_MAX_CHARS)}`);
  }
  const c = input.contact ? normalizeUserContact(input.contact) : undefined;
  if (c) {
    const flat = (s: string): string => s.replace(/\s*\n\s*/g, ', ');
    // Addresses render primary-first, each on its own line, so a form-fill picks the default first.
    for (const a of orderedEntries(c.addresses)) {
      const kind = a.label ? `${a.label.toLowerCase()} address` : 'mailing address';
      const tag = a.primary && (c.addresses?.length ?? 0) > 1 ? ' (primary)' : '';
      lines.push(`The user's ${kind}${tag}: ${flat(a.value)}`);
    }
    const phones = orderedEntries(c.phones).map((p) => {
      const tag = p.primary && (c.phones?.length ?? 0) > 1 ? ' (primary)' : '';
      return `${p.label || 'phone'} ${p.value}${tag}`;
    });
    if (phones.length) lines.push(`The user's phone numbers: ${phones.join(', ')}.`);
  }
  if (input.ownedEmails.length) {
    lines.push(
      `The user's own email addresses (mail from these is the user themself): ${input.ownedEmails.join(', ')}.`,
    );
  }
  if (acting) {
    lines.push(`You are composing AS the mailbox ${acting.email} — write in the first person as its owner.`);
    if (acting.identity?.signoff) lines.push(`Sign off exactly as: ${acting.identity.signoff}`);
  }
  if (!lines.length) return '';
  return `User context:\n${lines.join('\n')}`;
}
